import React from "react"
import PropTypes from "prop-types"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import ProgramActions from "components/ProgramActions/ProgramActions"

import FormatterHelper from "helpers/formatter"
import pictureHelper from "helpers/picture"

import styles from "./ProgramDisplay.css"
import consts from "consts"

import selectChannel from "selectors/channels"
import i18n from "consts/i18n"
import { generateButtonText } from "./ProgramDisplay.helpers"
import { useOnCTAClick } from "helpers/CTA"
import selectAuthentication from "selectors/authentication"
import { LOCALE } from "consts/locale"

export default function ProgramDisplay({
  program,
  category,
  channel,
  fromChannel = false,
  isCharged = false,
  subCategory,
  isReplay,
  onScrollBeyondCTA,
}) {
  const channelOffer = useSelector(state => selectChannel(state)?.direct?.offer)
  const authentication = useSelector(selectAuthentication)

  const onCTAClick = useOnCTAClick({ program })

  const programTitle = [
    program.data.title,
    program.hasReplay() ? "en streaming & replay" : "en streaming",
    isCharged ? undefined : "gratuit",
    program.isAvailable() || channel.isCommercialized() ? `sur ${channel.getTitle()}` : undefined,
  ]
    .filter(Boolean)
    .join(" ")

  const imageAlt = [program.isAvailable() ? `${channel.getTitle()} -` : undefined, program.getTitle(), isReplay ? "en replay" : undefined]
    .filter(Boolean)
    .join(" ")

  const categoryUrl = category
    ? FormatterHelper.basic(consts.routes.category, { locale: LOCALE, categoryId: category.id, categorySlug: category.slug_seo })
    : FormatterHelper.basic(consts.routes.seoHome, { locale: LOCALE })

  const programIsUnavailable = !program?.pending && (!program?.data?.is_available || program?.getBroadcastDate() === i18n.not_broadcasted)

  const displayLockIcon = isCharged && !programIsUnavailable && (!authentication?.isAuthenticated || !!program?.data?.offer?.product_equivalence_code)
  const url = pictureHelper.replaceProtocol(program.getBackdropUrl())
  const isLive = program.isLive()

  return (
    <div className={styles.topContent}>
      <div
        className={styles.backdrop}
        style={{
          backgroundImage: `radial-gradient(at center top, rgba(27, 28, 32, 0) 0%, rgba(27, 28, 32, 1) 75%), linear-gradient(to top, rgba(27, 28, 32, 0) 80%, rgb(27, 28, 32) 100%), url("${url}")`,
        }}
      />
      <div className={styles.content}>
        <div className={styles.detailsWrapper}>
          <div className={styles.poster}>
            <img
              className={styles.posterPicture}
              src={pictureHelper.replaceProtocol(program.isAvailable() ? program.getImageUrl("large") : program.getImageUrl("large", "poster"))}
              alt={imageAlt}
              width={310}
              height={409}
            />
          </div>
          <div className={styles.details}>
            <div className={styles.programInfo}>
              {isLive ? (
                <div className={styles.posterChannelLive}>En {isReplay ? "replay" : "direct"}</div>
              ) : (
                !programIsUnavailable && <div className={styles.posterChannel}>{program.getBroadcastDate()}</div>
              )}
              {fromChannel && (
                <h1 className={styles.channelTitle}>
                  Regardez {channel.getTitle()} en {isReplay ? "replay" : "direct"}
                </h1>
              )}
            </div>
            {fromChannel ? (
              <h2 className={styles.title}>
                <Link to={program.getUrl()}>{programTitle}</Link>
              </h2>
            ) : (
              <h1 className={styles.title}>{programTitle}</h1>
            )}
            {programIsUnavailable && <div className={styles.programIsUnavailable}>{i18n.not_broadcasted}</div>}
            {isCharged && !programIsUnavailable && <div className={styles.chargedBundle}>Disponible dans une option payante</div>}
            <ul className={styles.channel}>
              {category && (
                <li>
                  <Link to={categoryUrl}>{category.label}</Link>
                </li>
              )}
              <li>{subCategory.label}</li>
              {fromChannel || !program.isAvailable() ? null : <li>{channel.getTitle()}</li>}
            </ul>
            <div className={styles.description}>{program.data.description}</div>
            <ProgramActions
              text={generateButtonText({ isLive, programIsUnavailable })}
              onScrollBeyondCTA={onScrollBeyondCTA}
              onClick={onCTAClick}
              programPrice={displayLockIcon ? program?.data?.offer?.price || channelOffer?.price || "" : ""}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

ProgramDisplay.propTypes = {
  category: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  channel: PropTypes.object.isRequired,
  fromChannel: PropTypes.bool,
  program: PropTypes.object.isRequired,
  isCharged: PropTypes.bool,
  subCategory: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  isReplay: PropTypes.bool,
  onScrollBeyondCTA: PropTypes.func,
}
